/**
 * Shared Web Audio graph for music, ambient wind, VO ducking, and one-shot SFX.
 */
export class AudioBus {
  private static instance: AudioBus | null = null;

  ctx: AudioContext | null = null;
  master: GainNode | null = null;
  musicGain: GainNode | null = null;
  ambientGain: GainNode | null = null;
  sfxGain: GainNode | null = null;
  voGain: GainNode | null = null;
  private unlocked = false;

  static get(): AudioBus {
    if (!AudioBus.instance) AudioBus.instance = new AudioBus();
    return AudioBus.instance;
  }

  async unlock(): Promise<AudioContext> {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!this.ctx) {
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      this.master.gain.value = 1;
      this.master.connect(this.ctx.destination);

      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.value = 0.22;
      this.musicGain.connect(this.master);

      this.ambientGain = this.ctx.createGain();
      this.ambientGain.gain.value = 0.04;
      this.ambientGain.connect(this.master);

      this.sfxGain = this.ctx.createGain();
      this.sfxGain.gain.value = 0.45;
      this.sfxGain.connect(this.master);

      this.voGain = this.ctx.createGain();
      this.voGain.gain.value = 0.9;
      this.voGain.connect(this.master);
    }
    if (this.ctx.state === 'suspended') await this.ctx.resume();
    this.unlocked = true;
    return this.ctx;
  }

  isUnlocked(): boolean {
    return this.unlocked && !!this.ctx;
  }

  /** Soft duck music while VO plays. */
  duckMusic(seconds = 2.4): void {
    if (!this.ctx || !this.musicGain) return;
    const g = this.musicGain.gain;
    const now = this.ctx.currentTime;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(0.08, now + 0.12);
    g.linearRampToValueAtTime(0.22, now + Math.max(0.8, seconds));
  }

  /** Title-screen bed: quieter music+wind before full start. */
  setTitleBed(active: boolean): void {
    if (!this.ctx || !this.musicGain || !this.ambientGain) return;
    const now = this.ctx.currentTime;
    const m = active ? 0.1 : 0.22;
    const a = active ? 0.055 : 0.035;
    this.musicGain.gain.cancelScheduledValues(now);
    this.ambientGain.gain.cancelScheduledValues(now);
    this.musicGain.gain.linearRampToValueAtTime(m, now + 0.6);
    this.ambientGain.gain.linearRampToValueAtTime(a, now + 0.6);
  }
}
